import { Check, Copy, Mail } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { INVITE_MANY_MAX, type InviteManyResult } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { Field } from '../../../../components/Field';
import { Textarea } from '../../../../components/Input';
import { PersonPicker } from '../../../../components/PersonPicker';
import { formatNumber } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { useColleagues, useInviteMany } from '../../api/users-api';
import { PaneFrame, PaneSection, type PaneMode } from './PaneFrame';
import { splitEmails } from './people-address';
import { EMPTY_ROLE_REACH, RoleReachFields, toAssignment, type RoleReachDraft } from './RoleReachFields';
import { INVITE_OUTCOMES, ResultList, inviteTotals, type ResultLine } from './ResultList';
import styles from './InvitePane.module.css';

/**
 * USER-03 in People's pane (Admin redesign 2026-11-02, People A): invite several people at once, one role, reach
 * and cover for all of them, with their manager and the interview-panel switch as before. Each address then says
 * what happened to it — invited, typed twice, already a user, already invited, not an email — and an invited
 * address offers its link to copy, for an Admin sitting beside the new colleague.
 *
 * No name is asked: one is made from the address, and the person corrects it when they accept.
 */
export function InvitePane({
  mode,
  invitedWaiting,
  onShowInvited,
  onClose,
}: {
  readonly mode: PaneMode;
  /** How many invites are waiting to be accepted, for the link to the Invited tile. */
  readonly invitedWaiting: number | undefined;
  readonly onShowInvited: () => void;
  readonly onClose: () => void;
}): ReactNode {
  const invite = useInviteMany();
  const colleagues = useColleagues();
  const showToast = useToasts((state) => state.show);
  const [emailsText, setEmailsText] = useState('');
  const [reach, setReach] = useState<RoleReachDraft>(EMPTY_ROLE_REACH);
  const [managerUserId, setManagerUserId] = useState<string | null>(null);
  const [isPanelEligible, setIsPanelEligible] = useState(false);
  const [results, setResults] = useState<readonly InviteManyResult[] | null>(null);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  const emails = splitEmails(emailsText);
  const assignment = toAssignment(reach);
  const isTooMany = emails.length > INVITE_MANY_MAX;
  const canSend = emails.length > 0 && !isTooMany && assignment !== null;

  const send = (): void => {
    if (!canSend || assignment === null) return;
    invite.mutate(
      {
        emails: [...emails],
        assignment,
        isPanelEligible,
        ...(managerUserId ? { managerUserId } : {}),
      },
      {
        onSuccess: (response) => {
          setResults(response.results);
          setCopiedEmail(null);
          // What was sent is done; what wasn't stays in the box to fix and send again.
          setEmailsText(
            response.results
              .filter((result) => result.outcome === 'invalid' || result.outcome === 'failed')
              .map((result) => result.email)
              .join('\n'),
          );
          showToast({
            tone: response.invited === response.results.length ? 'success' : 'warning',
            message: `${response.invited} ${response.invited === 1 ? 'invite' : 'invites'} sent`,
            ...(response.invited === response.results.length ? {} : { detail: 'Some addresses were not sent. See why beside each one.' }),
          });
        },
        onError: (error) => showToast({ tone: 'danger', message: error.message }),
      },
    );
  };

  const copy = (result: InviteManyResult): void => {
    if (!result.inviteUrl) return;
    void navigator.clipboard
      .writeText(result.inviteUrl)
      .then(() => {
        setCopiedEmail(result.email);
        showToast({ tone: 'success', message: 'Invite link copied' });
      })
      .catch(() => showToast({ tone: 'danger', message: "The link wasn't copied", detail: 'This browser blocked the clipboard.' }));
  };

  const lines: readonly ResultLine[] = (results ?? []).map((result, index) => ({
    key: `${index}-${result.email}`,
    name: result.email,
    outcome: INVITE_OUTCOMES[result.outcome].label,
    tone: INVITE_OUTCOMES[result.outcome].tone,
    message: result.message,
    ...(result.inviteUrl
      ? {
          action: (
            <Button
              variant="ghost"
              size="sm"
              icon={copiedEmail === result.email ? Check : Copy}
              aria-label={`Copy the invite link for ${result.email}`}
              title="Copy link"
              onClick={() => copy(result)}
            />
          ),
        }
      : {}),
  }));

  // Ctrl+Enter sends, as every form does (06-qol §2).
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    send();
  };

  return (
    <PaneFrame mode={mode} label="Invite people" title="Invite people" sub="Each gets an email with a link that works once." onClose={onClose} onKeyDown={onKeyDown}>
      <PaneSection label="Who and what">
        <Field
          label="Their emails"
          hint={`One or more, separated by commas or new lines. Up to ${INVITE_MANY_MAX} at a time.`}
          error={isTooMany ? `That's ${emails.length}. Invite ${INVITE_MANY_MAX} or fewer at a time.` : undefined}
          isRequired
        >
          {({ inputId, describedBy, isInvalid }) => (
            <Textarea
              id={inputId}
              aria-describedby={describedBy}
              isInvalid={isInvalid}
              autoFocus
              rows={4}
              value={emailsText}
              placeholder={'priya.das@nestgroup.com\nkamrul.hasan@nestgroup.com'}
              onChange={(event) => setEmailsText(event.target.value)}
            />
          )}
        </Field>
        {emails.length > 0 && (
          <p className={styles.count}>
            {formatNumber(emails.length)} {emails.length === 1 ? 'address' : 'addresses'}
          </p>
        )}

        <RoleReachFields value={reach} onChange={setReach} />

        <Field label="Their manager" isOptional>
          {({ inputId }) => (
            <PersonPicker
              id={inputId}
              label="Their manager"
              people={colleagues.data ?? []}
              value={managerUserId}
              onChange={setManagerUserId}
              placeholder="Choose their manager"
              isClearable
            />
          )}
        </Field>

        <Checkbox label="Can sit on interview panels" checked={isPanelEligible} onChange={setIsPanelEligible} />

        <div className={styles.actions}>
          {invitedWaiting !== undefined && invitedWaiting > 0 && (
            <button type="button" className={styles.waiting} onClick={onShowInvited}>
              {formatNumber(invitedWaiting)} waiting to accept
            </button>
          )}
          <span className={styles.grow} />
          <Button variant="solid" icon={Mail} disabled={!canSend} isPending={invite.isPending} onClick={send}>
            {emails.length > 1 ? `Send ${formatNumber(emails.length)} invites` : 'Send the invite'}
          </Button>
        </div>
      </PaneSection>

      {results !== null && (
        <PaneSection>
          <ResultList title="What happened" totals={inviteTotals(results)} lines={lines} onDismiss={() => setResults(null)} />
        </PaneSection>
      )}
    </PaneFrame>
  );
}
